import React, { useEffect, useRef, useState } from "react";
import { Megaphone } from "lucide-react";
import { useAnalytics } from "../../utils/Customer/useAnalytics";

interface BannerProps {
  imageUrl?: string;
  alt?: string;
  sponsoredText?: string;
  bannerId?: number;
  onClick?: () => void;
}

const PetPromoBanner: React.FC<BannerProps> = ({
  imageUrl = "/images/pet-banner.webp",
  alt = "Pet Promotional Banner",
  sponsoredText = "Sponsored",
  bannerId,
  onClick,
}) => {
  const { trackBannerView, trackBannerClick } = useAnalytics();

  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // ------------------------------------------------------------
  // Banner visibility
  // ------------------------------------------------------------

  useEffect(() => {
    const element = bannerRef.current;

    if (!element || !bannerId) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [bannerId]);

  // ------------------------------------------------------------
  // Banner View
  // ------------------------------------------------------------

  useEffect(() => {
    if (!isVisible || !bannerId) {
      return;
    }

    trackBannerView(bannerId);
  }, [isVisible, bannerId, trackBannerView]);

  // ------------------------------------------------------------
  // Banner Click
  // ------------------------------------------------------------

  const handleClick = () => {
    if (bannerId) {
      trackBannerClick(bannerId);
    }

    onClick?.();
  };

  return (
    <div ref={bannerRef} className="w-full">
      <div
        onClick={handleClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={(event) => {
          if (onClick && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            handleClick();
          }
        }}
        className="relative w-full overflow-hidden rounded-3xl shadow-lg cursor-pointer group hover:shadow-2xl transition-all duration-300"
      >
        {/* Sponsored Tag */}
        <div className="absolute top-4 right-4 z-10">
          <div className="flex items-center gap-1 rounded-full bg-black/70 backdrop-blur-md px-3 py-1.5 text-white shadow-md">
            <Megaphone size={14} />

            <span className="text-xs md:text-sm font-medium">
              {sponsoredText}
            </span>
          </div>
        </div>

        {/* Banner Image */}
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />

        {/* Optional Overlay */}
        <div className="absolute inset-0 bg-black/5 group-hover:bg-black/10 transition-all duration-300" />
      </div>
    </div>
  );
};

export default PetPromoBanner;
